'use client';

import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Card } from '@packages/shared/src/ui/Card/Card';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { useIntl } from 'react-intl';
import { usePriceFormater } from '@packages/shared/src/hooks/usePriceFormater/usePriceFormater';
import { TextWithDialog } from '@packages/modules/src/TextWithDialog/TextWithDialog.client';
import { useInstallmentCalculatorTracking } from '../../../Tracking/hooks/useInstallmentCalculatorTracking/useInstallmentCalculatorTracking';

export type IconInstallmentsAndLegalHintsProps = {
  /**
   * the single installment value from the calculation result as float (e.g. 13.37)
   */
  singleInstallment: number;
  /**
   * the currency string from the calculation result
   */
  currency: string;
  /**
   * open dialog handler
   */
  openInstallmentCalulatorHandler: () => void;
};

/**
 * Promotion component with an icon, installment information and legal hints
 *
 * component to promote the installment calculator in a layout version with an icon, installment information and legal hints
 *
 * */
export const IconInstallmentsAndLegalHints = ({
  singleInstallment,
  currency,
  openInstallmentCalulatorHandler,
}: IconInstallmentsAndLegalHintsProps) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('INSTALLMENTCALCULATOR');

  const { trackGlycerinLinkClick } = useInstallmentCalculatorTracking();

  return (
    <Card className="bg-color-grey-light space-y-1.5 p-1.5">
      <div className="flex flex-nowrap gap-1.5">
        <div className="bg-color-additional flex h-6 w-6 items-center justify-center rounded">
          <span className="icon-emp-installments bg-color-background-base h-4 w-4" />
        </div>
        <div className="space-y-1">
          <p className="text-size-body-base">
            {formatMessage(
              {
                defaultMessage: 'oder nur {value} pro Monat',
                id: 'infoElementUnito.rate-text',
              },
              {
                value: formatPrice(singleInstallment, currency),
              },
            )}
          </p>
          <Button
            onClick={() => {
              openInstallmentCalulatorHandler();
              trackGlycerinLinkClick();
            }}
          >
            {formatMessage({
              defaultMessage: 'Finden Sie jetzt Ihre Wunschrate',
              id: 'infoElementUnito.button-text',
            })}
          </Button>
        </div>
      </div>
      <Divider flexItem />
      <p className="text-size-body-base">
        {formatMessage(
          {
            defaultMessage:
              'Die gesetzlichen Informationen zum Teilzahlungsgeschäft finden Sie <button>hier</button>.',
            id: 'infoElementUnito.legal-text',
          },
          {
            button: (msg) => (
              <TextWithDialog
                message={msg.join(' ')}
                dialogTitle={formatMessage({
                  defaultMessage: 'Flexikonto Teilzahlung',
                  id: 'infoElementUnito.modal.headline',
                })}
                dialogContent={<ServiceLayer serviceLayerId="installment_ratenelement" />}
                displayTrackingProps={{ category: 'dv-service-layer-layout-3' }}
                aria-label={formatMessage({
                  defaultMessage:
                    'Die gesetzlichen Informationen zum Teilzahlungsgeschäft finden Sie hier.',
                  id: 'infoElementUnito.legal-text.aria',
                })}
              />
            ),
          },
        )}
      </p>
    </Card>
  );
};
